import React, {useCallback, useEffect, useState} from 'react';
import {connect} from "react-redux";
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle, FormControlLabel, Step, StepLabel, Stepper, Switch, TextField,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {websiteDomainsActions} from "../../store/actions/websiteDomainsAction";
import {useHistory, useLocation} from "react-router-dom";
import {useForm} from "react-hook-form";
import WorkspaceDropdown from "../workspace/WorkspaceDropdown";
import IntegrationDropdown from "./IntegrationDropdown";
import {alertActions} from "../../store/actions/alertActions";

import {WebsiteDialog as Dialog, WebsiteCnameRecords} from "./WebsitesDesign";
import {SettingsDialogDescription} from "../settings/SettingsDialog";

function DomainsAdd(props) {

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const history = useHistory();
    const location = useLocation();

    const [autoVerify, setAutoVerify] = useState(false);

    const {register, handleSubmit, control, setValue, formState: {errors}, setError} = useForm();

    const {getDomainVerifyRecords, addDomain, updateActiveStep, alertError, stopLoading} = props;
    const {steps, activeStep, newDomainAdded, newDomainId} = props?.websiteDomainsState;
    const {plan} = props?.auth?.subscription;

    const handleClose = useCallback(() => {
        updateActiveStep(1);
        history.push({pathname: "/websites/domains", state: {from: location}});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, location]);

    const getSubdomain = (domain) => {
        if (domain === "") {
            return "";
        }
        const parts = domain?.split(".");
        // If the domain has fewer than 3 parts, it doesn't have a subdomain
        if (parts?.length < 3) {
            return "";
        }
        // Return everything except the last two parts (which are the domain and TLD)
        return parts?.slice(0, parts?.length - 2).join(".");
    }

    const handleOnSubmitStep1 = (data) => {
        if (data?.domainIntegration?.networkType === "server_namesilo" && getSubdomain(data?.domain) === "") {
            setError("domain", {
                type: "manual",
                message: "You will need to add \"www\" for SiloName domains to get root domain working with CNAME.",
            });
            return false;
        }
        addDomain(data);
    };

    // This feature is not available with Free plan.
    useEffect(() => {
        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
            return false;
        }
    });

    // Waiting until new domain is added.
    useEffect(() => {
        if (newDomainAdded && !autoVerify) {
            setTimeout(() => {
                updateActiveStep(2);
            }, 7000);
        }
        if (newDomainAdded && autoVerify) {
            stopLoading();
            handleClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newDomainAdded]);

    // Load domain CNAME records.
    useEffect(() => {
        if (activeStep === 2) {
            getDomainVerifyRecords(newDomainId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep]);

    return (
        <Dialog
            loading={props?.websiteDomainsState?.isLoading}
            open={true}
            onClose={handleClose}
            fullWidth={true}
            fullScreen={fullScreen}
            aria-labelledby="cname-records-dialog"
            maxWidth="sm"
        >
            <DialogTitle id="cname-records-dialog">Add domain
                <SettingsDialogDescription>Connect a domain and configure its SSL certificate.</SettingsDialogDescription>
            </DialogTitle>
            <DialogContent>
                <Stepper
                    activeStep={activeStep - 1}
                >
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {activeStep === 1 &&
                    <>
                        <form id="website-form" onSubmit={handleSubmit(handleOnSubmitStep1)} autoComplete={"off"}>
                            <Box className="website-form-fields">
                                <Box p={1} width="100%">
                                    <WorkspaceDropdown control={control} errors={errors} setValue={setValue}/>
                                </Box>
                                <Box p={1} width="100%">
                                    <IntegrationDropdown
                                        networkTypes={["server_aws"]}
                                        control={control}
                                        label="AWS Integration*"
                                        name="serverIntegration"
                                        setRequestError={alertError}
                                        error={!!errors?.serverIntegration}
                                    />
                                </Box>
                                <Box p={1} width="100%">
                                    <TextField
                                        error={!!errors.domain}
                                        variant="outlined"
                                        margin="dense"
                                        id="domain"
                                        label="Domain*"
                                        {...register("domain", {
                                            required: {
                                                value: true,
                                                message: "Domain is mandatory field."
                                            },
                                            maxLength: 100,
                                            minLength: 4,
                                            pattern: {
                                                value: /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i,
                                                message: "Invalid domain name."
                                            }
                                        })}
                                        name="domain"
                                        size="small"
                                        type="text"
                                        placeholder="Enter your domain or subdomain (e.g. example.com or app.example.com)"
                                        fullWidth={true}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                    {errors?.domain &&
                                        <Box sx={{color: "error.main"}}>{errors?.domain?.message}</Box>
                                    }
                                </Box>
                                <Box p={1} width="100%">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={autoVerify}
                                                color="primary"
                                                onChange={(e, checked) => setAutoVerify(checked)}
                                            />
                                        }
                                        label="Verify domain automatically"
                                    />

                                </Box>
                                {autoVerify &&
                                    <Box p={1} width="100%">
                                        <IntegrationDropdown
                                            control={control}
                                            errors={errors}
                                            label="Your domain provider*"
                                            name="domainIntegration"
                                            setRequestError={alertError}
                                            error={!!errors?.domainIntegration}
                                        />
                                    </Box>
                                }
                            </Box>
                        </form>
                    </>
                }

                {activeStep === 2 && <WebsiteCnameRecords record={props?.websiteDomainsState?.cnameRecord}/>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="inherit" variant="outlined" disabled={props?.websiteDomainsState?.isLoading}>Close</Button>
                {activeStep === 1 && <Button type="submit" form="website-form" color="primary" variant="contained" disabled={props?.websiteDomainsState?.isLoading}>Next</Button>}
            </DialogActions>
        </Dialog>
    );
}

const mapStateToProps = (state) => {
    return {
        websiteDomainsState: state.websiteDomains,
        auth: state.auth
    }
};

const mapDispatchToProps = {
    getDomainVerifyRecords: websiteDomainsActions.getDomainVerifyRecords,
    addDomain: websiteDomainsActions.addDomain,
    stopLoading: websiteDomainsActions.stopLoading,
    updateActiveStep: websiteDomainsActions.updateActiveStep,
    alertError: alertActions.error
};

export default connect(mapStateToProps, mapDispatchToProps)(DomainsAdd);
